import { copyText } from "~/common/utils"
import { useState } from "react"
import { CopyOutlined, ExperimentOutlined } from "@ant-design/icons"
import { Button, Empty, Flex, Form, Input, Typography, message } from "antd"
import { getStaffVideoFrame } from "~/api/staff"
import styles from "./index.css"

const { Link, Text, Title } = Typography

type LabFormValues = {
  videoUrl: string
}

export const StaffLab = () => {
  const [form] = Form.useForm<LabFormValues>()
  const [cdnUrl, setCdnUrl] = useState("")
  const [submitting, setSubmitting] = useState(false)

  const submit = async ({ videoUrl }: LabFormValues) => {
    setSubmitting(true)
    setCdnUrl("")

    try {
      const response = await getStaffVideoFrame({ video_url: videoUrl.trim() })
      const nextCdnUrl = response.data?.cdn_url

      if (!nextCdnUrl) {
        throw new Error("接口未返回分镜图片地址")
      }

      setCdnUrl(nextCdnUrl)
      void message.success("分镜生成成功")
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "分镜生成失败，请稍后重试"
      void message.error(errorMessage)
    } finally {
      setSubmitting(false)
    }
  }

  const copyCdnUrl = async () => {
    if (!cdnUrl) return

    if (await copyText(cdnUrl)) {
      void message.success("图片链接已复制")
    } else {
      void message.error("复制失败，请手动复制链接")
    }
  }

  return (
    <Flex
      className={styles.page}
      vertical
      gap={18}
    >
      <Flex
        vertical
        gap={6}
      >
        <Title level={2}>实验室</Title>
        <Text type="secondary">输入视频 URL，提取视频分镜并生成拼接图。</Text>
      </Flex>

      <section className={styles.toolPanel}>
        <Form<LabFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => void submit(values)}
        >
          <Flex
            align="flex-start"
            gap={12}
          >
            <Form.Item
              className={styles.urlField}
              name="videoUrl"
              label="视频 URL"
              rules={[
                { required: true, whitespace: true, message: "请输入视频 URL" },
                { type: "url", message: "请输入有效的视频 URL" },
              ]}
            >
              <Input
                allowClear
                placeholder="https://example.com/video.mp4"
                suffix={<ExperimentOutlined />}
              />
            </Form.Item>
            <Button
              className={styles.submitButton}
              htmlType="submit"
              loading={submitting}
              type="primary"
            >
              确认
            </Button>
          </Flex>
        </Form>
      </section>

      <section className={styles.resultPanel}>
        <Title level={4}>分镜结果</Title>
        {cdnUrl ? (
          <Flex
            className={styles.resultCard}
            vertical
            gap={12}
          >
            <button
              className={styles.imageButton}
              title="点击复制图片链接"
              type="button"
              onClick={() => void copyCdnUrl()}
            >
              <img
                className={styles.resultImage}
                alt="视频分镜拼接结果"
                src={cdnUrl}
              />
              <span className={styles.copyHint}>
                <CopyOutlined /> 点击图片复制链接
              </span>
            </button>
            <Link
              className={styles.resultLink}
              ellipsis
              href={cdnUrl}
              rel="noreferrer"
              target="_blank"
              title={cdnUrl}
            >
              {cdnUrl}
            </Link>
          </Flex>
        ) : (
          <Empty description="提交视频 URL 后展示分镜结果" />
        )}
      </section>
    </Flex>
  )
}
